import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { Button, CollapseCard, FakeText } from '@totalsoft/rocket-ui';
import { Grid } from '@mui/material';
import { emptyArray } from 'constants/defaultValues';
import { get } from '@totalsoft/change-tracking-react';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InsurersContainer from './InsurersContainer';
import PeriodsContainer from './PeriodsContainer';
import RcaComponent from './RcaComponent';
import CascoComponent from './CascoComponent';
import InsuranceDataSummary from './InsuranceDataSummary';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import FakeTextWrapper from 'components/common/FakeTextWrapper';

const InsuranceDataCard = ({
  loadingStatus,
  insuranceDataLens,
  insurersLens,
  resetInsurersLens,
  periodsLens,
  resetPeriodsLens,
  selectedInsurers,
  validationInsuranceData,
  insuranceTypeCode,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  financingTypesData,
  activityTypesData,
  periods,
  currencyData,
  saving,
  saveDisabled,
  selectedPeriods,
  onSave,
  onFinancingTypeChange,
  showActivityField,
  summaryData,
  readOnly,
  hasExternalBeneficiary
}) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditInsuranceData = useAuthorization(AuthorizationCodes.hasRightToEditInsuranceData, documentTypeId, documentStateId);
  const { t } = useTranslation();

  return (
    <CollapseCard
      id='insuranceDataCard_CollapseCard'
      icon={ListAltIcon}
      title={t('Offer.Edit.InsuranceData.Title')}
      subheader={<InsuranceDataSummary summaryData={summaryData} loading={loadingStatus.insuranceDataLoading} />}
      hideSubheaderOnExpand
      toggleOnHeaderClick
    >
      <Grid container px={1}>
        {!loadingStatus.loadingCompleted ? (
          <FakeTextWrapper />
        ) : (
          <>
            <InsurersContainer
              resetInsurersLens={resetInsurersLens}
              insurersLens={insurersLens}
              selectedInsurers={selectedInsurers}
              validationInsurers={validationInsuranceData.insurers}
              insuranceTypeId={insuranceDataLens.insuranceTypeId |> get}
              readOnly={readOnly({
                controlName: 'InsuranceData.Insurers',
                hasAuthorization: hasRightToEditInsuranceData
              })}
            />
            {insuranceTypeCode ? (
              <>
                {insuranceTypeCode === rcaInsuranceTypeCode && (
                  <>
                    <PeriodsContainer
                      resetPeriodsLens={resetPeriodsLens}
                      periodsLens={periodsLens}
                      selectedPeriods={selectedPeriods}
                      validationPeriods={validationInsuranceData.periods}
                      insuranceTypeCode={insuranceTypeCode}
                      readOnly={readOnly({
                        controlName: 'InsuranceData.Periods',
                        hasAuthorization: hasRightToEditInsuranceData
                      })}
                    />
                    <RcaComponent
                      insuranceDataLens={insuranceDataLens}
                      financingTypes={financingTypesData || emptyArray}
                      activityTypes={activityTypesData}
                      periods={periods}
                      validationInsuranceData={validationInsuranceData}
                      onFinancingTypeChange={onFinancingTypeChange}
                      showActivityField={showActivityField}
                      readOnly={readOnly}
                    />
                  </>
                )}
                {insuranceTypeCode === cascoInsuranceTypeCode && (
                  <>
                    <PeriodsContainer
                      resetPeriodsLens={resetPeriodsLens}
                      periodsLens={periodsLens}
                      selectedPeriods={selectedPeriods}
                      validationPeriods={validationInsuranceData.periods}
                      insuranceTypeCode={insuranceTypeCode}
                      readOnly={readOnly({
                        controlName: 'InsuranceData.Periods',
                        hasAuthorization: hasRightToEditInsuranceData
                      })}
                    />
                    <CascoComponent
                      insuranceDataLens={insuranceDataLens}
                      financingTypes={financingTypesData || emptyArray}
                      periods={periods}
                      currencies={currencyData?.currencies}
                      validationInsuranceData={validationInsuranceData}
                      onFinancingTypeChange={onFinancingTypeChange}
                      hasExternalBeneficiary={hasExternalBeneficiary}
                      readOnly={readOnly}
                    />
                  </>
                )}
              </>
            ) : (
              <FakeText lines={5} />
            )}
          </>
        )}
        <Grid container mt={2}>
          <Grid item xs={12}>
            <Button color='primary' onClick={onSave} disabled={saveDisabled || !hasRightToEditInsuranceData} id='insuranceData_Save'>
              {saving ? t('General.Saving') : t('General.Save')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

InsuranceDataCard.propTypes = {
  loadingStatus: PropTypes.object.isRequired,
  insuranceDataLens: PropTypes.object.isRequired,
  insurersLens: PropTypes.object.isRequired,
  resetInsurersLens: PropTypes.func.isRequired,
  periodsLens: PropTypes.object.isRequired,
  resetPeriodsLens: PropTypes.func.isRequired,
  selectedInsurers: PropTypes.array,
  validationInsuranceData: PropTypes.object.isRequired,
  insuranceTypeCode: PropTypes.string,
  rcaInsuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  financingTypesData: PropTypes.array.isRequired,
  activityTypesData: PropTypes.array,
  periods: PropTypes.array.isRequired,
  currencyData: PropTypes.object,
  saving: PropTypes.bool.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  selectedPeriods: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onFinancingTypeChange: PropTypes.func,
  showActivityField: PropTypes.bool,
  summaryData: PropTypes.object.isRequired,
  readOnly: PropTypes.func.isRequired,
  hasExternalBeneficiary: PropTypes.bool
};

export default InsuranceDataCard;
